import { useState, type FormEvent } from 'react'
import AppLayout from '../components/AppLayout'
import TwoFactorEnrollment from '../components/TwoFactorEnrollment'
import { Alert, Badge, Button, Card, Field } from '../components/ui'
import { formatDateTime } from '../lib/admin'
import { useDisableMfa, useProfile, type Profile } from '../lib/profile'
import RequireSession from './RequireSession'

export default function ProfilePage() {
  return (
    <RequireSession>
      {(user) => (
        <AppLayout user={user}>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
            Mon profil
          </h1>
          <ProfileContent />
        </AppLayout>
      )}
    </RequireSession>
  )
}

function ProfileContent() {
  const profile = useProfile()

  if (profile.isPending) {
    return <p className="mt-6 text-sm text-neutral-500">Chargement...</p>
  }

  if (profile.isError) {
    return (
      <div className="mt-6">
        <Alert>{profile.error.message}</Alert>
      </div>
    )
  }

  const data = profile.data

  return (
    <div className="mt-6 space-y-6">
      <Card title="Informations">
        <dl className="grid gap-3 text-sm sm:grid-cols-[12rem_1fr]">
          <dt className="text-neutral-500 dark:text-neutral-400">Nom affiche</dt>
          <dd className="text-neutral-900 dark:text-neutral-100">{data.displayName}</dd>
          <dt className="text-neutral-500 dark:text-neutral-400">Adresse e-mail</dt>
          <dd className="text-neutral-900 dark:text-neutral-100">{data.email}</dd>
          <dt className="text-neutral-500 dark:text-neutral-400">Role</dt>
          <dd>{data.roles.length > 0 ? data.roles.join(', ') : 'Utilisateur'}</dd>
          <dt className="text-neutral-500 dark:text-neutral-400">Compte cree le</dt>
          <dd>{formatDateTime(data.createdAt)}</dd>
        </dl>
      </Card>

      <TwoFactorCard profile={data} />
    </div>
  )
}

function TwoFactorCard({ profile }: { profile: Profile }) {
  const [enrolling, setEnrolling] = useState(false)

  return (
    <Card title="Verification en deux etapes">
      <div className="mb-4 flex items-center gap-2 text-sm">
        <span className="text-neutral-600 dark:text-neutral-400">Etat :</span>
        {profile.twoFactorEnabled ? (
          <Badge tone="ok">Active</Badge>
        ) : (
          <Badge tone="warn">Desactivee</Badge>
        )}
      </div>

      {profile.twoFactorEnabled && !enrolling ? (
        <DisableTwoFactor recoveryCodesLeft={profile.recoveryCodesLeft} />
      ) : (
        <>
          {!enrolling && (
            <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">
              En plus du mot de passe, un code a six chiffres genere par votre telephone vous sera
              demande a chaque connexion.
            </p>
          )}
          {/* Garde affiche tant que `enrolling` est vrai : une fois le
              second facteur active, le profil recharge le dit actif, mais
              les codes de secours doivent rester a l'ecran jusqu'a ce que
              l'utilisateur confirme les avoir notes. */}
          <TwoFactorEnrollment
            onStarted={() => setEnrolling(true)}
            onDone={() => setEnrolling(false)}
          />
        </>
      )}
    </Card>
  )
}

function DisableTwoFactor({ recoveryCodesLeft }: { recoveryCodesLeft: number }) {
  const disable = useDisableMfa()
  const [code, setCode] = useState('')

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    disable.mutate(code, { onSuccess: () => setCode('') })
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        Codes de secours restants : {recoveryCodesLeft}. Pour desactiver la verification, saisissez
        le code actuellement affiche par votre application.
      </p>
      <form onSubmit={onSubmit} className="max-w-xs space-y-3">
        <Field
          label="Code a six chiffres"
          name="code"
          autoComplete="one-time-code"
          inputMode="numeric"
          required
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        {disable.isError && <Alert>{disable.error.message}</Alert>}
        <Button type="submit" size="sm" variant="danger" disabled={disable.isPending}>
          {disable.isPending ? 'Desactivation...' : 'Desactiver'}
        </Button>
      </form>
    </div>
  )
}
